/**
 * ScrollProgress Component
 *
 * A subtle reading progress indicator that shows how far the user has scrolled down the page.
 * Uses a gradient bar at the top of the viewport with smooth animations.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), var(--secondary), var(--accent));
  transform-origin: left;
  transform: scaleX(${props => props.$progress});
  z-index: 10000;
  transition: transform 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  box-shadow: 0 0 10px rgba(184, 187, 38, 0.5);

  /* Add subtle glow animation */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3));
    opacity: ${props => props.$progress > 0 ? 1 : 0};
    transition: opacity 0.3s ease;
  }
`;

/**
 * ScrollProgress component that displays a progress bar at the top of the page
 * The bar fills as the user scrolls down, providing visual feedback on reading progress
 *
 * @returns {JSX.Element} The scroll progress indicator
 */
const ScrollProgress = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    /**
     * Calculate scroll progress as a value between 0 and 1
     * Based on how far the user has scrolled through the document
     */
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Calculate the maximum scrollable height
      const maxScroll = documentHeight - windowHeight;

      // Calculate progress (0 to 1)
      const progress = maxScroll > 0 ? scrollTop / maxScroll : 0;

      setScrollProgress(progress);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Call once to set initial state
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return <ProgressBar $progress={scrollProgress} />;
};

export default ScrollProgress;
